import { useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Navigation } from '../Navigation/Navigation';
import { Toaster } from 'react-hot-toast';
import { User, ShieldAlert } from 'lucide-react';
import { useAppStore } from '../../store/useAppStore';

export function Layout() {
  const user = useAppStore((state) => state.user);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle pending ticket deep link navigation
  useEffect(() => {
    const pendingTicketId = sessionStorage.getItem('gemifaucet_pending_ticket');
    if (pendingTicketId) {
      console.log('[Layout] Navigating to support for pending ticket:', pendingTicketId);
      // Navigate to support page - the Support component will handle opening the ticket
      navigate('/support');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen bg-background text-white flex flex-col">
      {/* Toast notifications */}
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: '#1e1e2e',
            color: '#fff',
            borderRadius: '12px',
            border: '1px solid rgba(255,255,255,0.1)',
          },
          success: {
            iconTheme: {
              primary: '#22c55e',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
          },
        }}
      />

      {/* Banned Banner */}
      {user?.isBanned && (
        <div className="bg-red-500/90 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm">
          <ShieldAlert className="w-4 h-4" />
          <span className="font-medium">Your account has been suspended</span>
          <Link
            to="/support"
            className="ml-3 px-2 py-1 bg-blue-500/80 text-white text-xs rounded hover:bg-blue-600 transition-colors font-semibold shadow"
            style={{ verticalAlign: 'middle' }}
          >
            Open Support Ticket
          </Link>
        </div>
      )}

      {/* Top Header Bar */}
      <header className="sticky top-0 z-40 bg-surface/95 backdrop-blur-lg border-b border-white/5">
        <div className="container mx-auto px-4 max-w-lg">
          <div className="flex items-center justify-between h-12">
            <div className="flex items-center gap-2">
              <span className="text-lg font-bold gradient-text">GEMI</span>
              <span className="text-[10px] text-white/40 bg-white/5 px-1.5 py-0.5 rounded">BETA</span>
            </div>
            <Link 
              to="/profile" 
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
            >
              <div className={`relative w-6 h-6 rounded-full bg-gradient-to-br ${user?.isBanned ? 'from-red-500 to-red-600' : 'from-primary to-accent'} flex items-center justify-center`}>
                <User className="w-3.5 h-3.5 text-white" />
                {user?.isBanned && (
                  <div className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-surface" />
                )}
              </div>
              <span className="text-xs text-white/70">{user?.firstName || 'Profile'}</span>
              {user?.isBanned && (
                <span className="text-[10px] bg-red-500/20 text-red-400 px-1.5 py-0.5 rounded font-medium">
                  Banned
                </span>
              )}
            </Link>
          </div>
        </div>
      </header>

      {/* Main content area */}
      <main className="flex-1 overflow-y-auto pb-20">
        <div className="container mx-auto px-4 pt-2 pb-4 max-w-lg">
          <AnimatePresence mode="wait">
            <motion.div
              key={location.pathname}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <Outlet />
            </motion.div>
          </AnimatePresence>
        </div>
      </main>

      {/* Bottom navigation */}
      <Navigation />
    </div>
  );
}
